import { JEFE_1 } from '../../../data/boss-objects';
import { SIM_HZ, VIEW_WIDTH } from '../../../data/tuning';
import { createHelicopter, stepHelicopter } from '../../enemies/law-helicopter';
import { spawnObject, ticksOf, type PatternEnv } from '../attacks';
import type { BossFight } from '../boss-fight';
import { registerPattern } from '../patterns';
import type { TickContext } from '../../tick';

/**
 * Attack patterns of JEFE-1 El Patrón (GDD §9.2, issue #95). Each is called once per tick while its phase is
 * active and the boss is not in transition. The counters restart when the phase changes.
 */

/** Restarts the counters of the attack field when the boss moved to another phase. */
function syncPhase(fight: BossFight, env: PatternEnv): void {
  if (env.attacks.phase === fight.phase) return;
  env.attacks.phase = fight.phase;
  env.attacks.cadence = 0;
  env.attacks.pending = [];
}

/** One column of the rain: a silver coin or a lead, falling vertically from above the ceiling. */
function rain(context: TickContext, env: PatternEnv, factor: number): void {
  const { attacks, view } = env;
  attacks.cadence += 1;
  if (attacks.cadence < ticksOf(JEFE_1.rainIntervalSeconds / factor)) return;
  attacks.cadence = 0;
  const x = view.left + (JEFE_1.rainFromView + attacks.rng() * (JEFE_1.rainToView - JEFE_1.rainFromView)) * VIEW_WIDTH;
  const type = attacks.rng() < JEFE_1.silverShare ? 'silver-coin' : 'lead';
  spawnObject(attacks, context, type, { x, y: JEFE_1.rainSpawnY, vy: -(JEFE_1.rainFallSpeed * factor) / SIM_HZ });
}

/** Phase 1, "¿Plata o plomo?". */
registerPattern('plata-o-plomo', (fight, context, env) => {
  if (!env) return;
  syncPhase(fight, env);
  rain(context, env, 1);
});

/** Phase 2, "Nápoles": a burst of two plush hippos, tossed from the boss towards the plane, every few seconds. */
registerPattern('napoles', (fight, context, env) => {
  if (!env) return;
  syncPhase(fight, env);
  const { attacks, run } = env;
  attacks.cadence += 1;
  if (attacks.cadence >= ticksOf(JEFE_1.napolesBurstSeconds)) {
    attacks.cadence = 0;
    attacks.pending = JEFE_1.hippoSpeeds.slice(0, JEFE_1.hippoPerBurst).map((speed, index) => ({ delay: index * ticksOf(JEFE_1.hippoSpacingSeconds), speed }));
    context.emit({ type: 'boss:burst', bossId: fight.def.id, count: attacks.pending.length });
  }
  for (const launch of attacks.pending) {
    if (launch.delay-- > 0) continue;
    // Relative to the scroll the hippo flies towards the plane; in the world the scroll adds to its speed.
    spawnObject(attacks, context, 'plush-hippo', {
      x: fight.x,
      y: fight.y,
      vx: (run.scrollSpeed - launch.speed) / SIM_HZ,
      vy: JEFE_1.hippoLaunchVy / SIM_HZ,
      gravity: JEFE_1.hippoGravity / (SIM_HZ * SIM_HZ),
    });
  }
  attacks.pending = attacks.pending.filter((launch) => launch.delay >= 0);
});

/** Phase 3, "Bloque de Búsqueda": the rain of phase 1 faster, with a law helicopter (EN-02) sweeping the back. */
registerPattern('bloque-de-busqueda', (fight, context, env) => {
  if (!env) return;
  syncPhase(fight, env);
  const { attacks, run, view } = env;
  rain(context, env, JEFE_1.phase3SpeedFactor);
  attacks.helicopter ??= createHelicopter(JEFE_1.helicopterY);
  const helicopter = attacks.helicopter;
  const at = {
    x: view.left + JEFE_1.helicopterFromView * VIEW_WIDTH,
    y: JEFE_1.helicopterY + helicopter.arrivalLeft,
  };
  if (helicopter.arrivalLeft > 0) helicopter.arrivalLeft = Math.max(0, helicopter.arrivalLeft - 4 / SIM_HZ);
  const step = stepHelicopter(helicopter, at, run.core.player, run.core.bribeRemaining > 0);
  if (step.amber) context.emit({ type: 'en02:amber', source: 'boss' });
  if (step.burst) context.emit({ type: 'en02:burst', source: 'boss', bursts: helicopter.bursts });
});

